import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Chip,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Paper,
  Divider,
  Button,
  Stack,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  Link as LinkIcon,
  Favorite as FavoriteIcon,
  ShoppingBag as ShoppingBagIcon,
  PeopleAlt as PeopleAltIcon,
  PendingActions as PendingActionsIcon,
} from '@mui/icons-material';
import { providerApi } from '../../services/api';

// Users who have an active or completed order relationship
const CLIENT_STATUSES = new Set(['CONFIRMED', 'PAID', 'PAID_AND_READY', 'FINISHED', 'COMPLETED']);
// Users still in the negotiation / pending-payment funnel — potential clients
const LEAD_STATUSES = new Set(['CREATED', 'ACTIVE', 'DISCUSSION', 'DISCUSSIONED']);
const ORDERS_FETCH_SIZE = 500;

// ── Merged audience member ───────────────────────────────────────────────────
function buildAudienceMap(affiliates, likers, orders) {
  const map = new Map();

  const upsert = (userId, defaults) => {
    if (!map.has(userId)) map.set(userId, { ...defaults, isAffiliated: false, referredAt: null, isFan: false, isLead: false, isClient: false, orders: [] });
    return map.get(userId);
  };

  for (const a of affiliates) {
    const m = upsert(a.userId, { userId: a.userId, name: a.name, picture: a.picture, city: a.city, country: a.country });
    m.isAffiliated = true;
    m.referredAt = a.referredAt;
  }

  for (const l of likers) {
    const m = upsert(l.userId, { userId: l.userId, name: l.name, picture: l.picture, city: l.city, country: l.country });
    m.isFan = true;
  }

  for (const order of orders) {
    if (!order.user?.id) continue;
    const isClient = CLIENT_STATUSES.has(order.status);
    const isLead = LEAD_STATUSES.has(order.status);
    if (!isClient && !isLead) continue;
    const m = upsert(order.user.id, { userId: order.user.id, name: order.user.name, picture: order.user.picture, city: null, country: null });
    if (isClient) m.isClient = true;
    if (isLead) m.isLead = true;
    m.orders.push(order);
  }

  return [...map.values()].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
}

// ── Single expandable user card ──────────────────────────────────────────────
function AudienceCard({ member, onNavigateToOrders, showOrders }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const initials = member.name?.charAt(0)?.toUpperCase() ?? '?';

  const latestOrder = member.orders.length > 0
    ? [...member.orders].sort((a, b) => new Date(b.createdAt ?? 0) - new Date(a.createdAt ?? 0))[0]
    : null;

  const location = [member.city, member.country].filter(Boolean).join(', ');

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded((v) => !v)}
      elevation={0}
      disableGutters
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '8px !important',
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, minHeight: '56px !important' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
          <Avatar src={member.picture ?? undefined} sx={{ width: 36, height: 36, fontSize: '0.85rem', flexShrink: 0 }}>
            {initials}
          </Avatar>
          <Typography variant="body2" fontWeight={600} noWrap sx={{ minWidth: 80, mr: 1 }}>
            {member.name}
          </Typography>
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', flex: 1 }}>
            {member.isAffiliated && (
              <Chip icon={<LinkIcon sx={{ fontSize: '14px !important' }} />} label={t('audience.affiliated')} size="small" color="secondary" variant="outlined" />
            )}
            {member.isFan && (
              <Chip icon={<FavoriteIcon sx={{ fontSize: '14px !important' }} />} label={t('audience.fan')} size="small" color="error" variant="outlined" />
            )}
            {member.isLead && (
              <Chip icon={<PendingActionsIcon sx={{ fontSize: '14px !important' }} />} label={t('audience.lead')} size="small" color="warning" variant="outlined" />
            )}
            {member.isClient && (
              <Chip icon={<ShoppingBagIcon sx={{ fontSize: '14px !important' }} />} label={t('audience.client')} size="small" color="primary" variant="outlined" />
            )}
          </Stack>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
        <Divider sx={{ mb: 2 }} />

        {/* Profile */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.disabled" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block' }}>
            {t('audience.profileSection')}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {location || '—'}
          </Typography>
        </Box>

        {/* Affiliated */}
        {member.isAffiliated && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.disabled" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block' }}>
              🔗 {t('audience.installedViaLink')}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {t('audience.referredOn')}{' '}
              {member.referredAt ? new Date(member.referredAt).toLocaleDateString() : '—'}
            </Typography>
          </Box>
        )}

        {/* Orders */}
        {(member.isClient || member.isLead) && latestOrder && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.disabled" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block' }}>
              📋 {t('audience.ordersSection', { count: member.orders.length })}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {t('audience.lastOrder')}{' '}
              {latestOrder.dogServiceType ?? latestOrder.orderType ?? '—'}
              {latestOrder.price != null ? ` · ${latestOrder.price} ${latestOrder.currencyCode ?? ''}`.trimEnd() : ''}
              {` · ${latestOrder.status}`}
              {latestOrder.createdAt ? ` · ${new Date(latestOrder.createdAt).toLocaleDateString()}` : ''}
            </Typography>
            {showOrders && onNavigateToOrders && (
              <Button size="small" onClick={onNavigateToOrders} sx={{ mt: 0.5, px: 0, textTransform: 'none' }}>
                {t('audience.viewAllOrders')}
              </Button>
            )}
          </Box>
        )}

        {/* Fan */}
        {member.isFan && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.disabled" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block' }}>
              ❤️ {t('audience.fan')}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {t('audience.likedYourProfile')}
            </Typography>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
const MyAudience = ({ providerData, onNavigateToOrders }) => {
  const { t } = useTranslation();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  const showOrders = providerData.providerType === 'SERVICE' || providerData.providerType === 'STORE';

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const [affiliatesRes, likersRes, ordersRes] = await Promise.allSettled([
        providerApi.getMyAffiliates(),
        providerApi.getMyLikers(),
        providerApi.getProviderOrders(providerData.id, 0, ORDERS_FETCH_SIZE),
      ]);
      if (cancelled) return;

      const errs = {};
      const affiliates = affiliatesRes.status === 'fulfilled' ? (affiliatesRes.value ?? []) : (errs.affiliates = true, []);
      const likers     = likersRes.status     === 'fulfilled' ? (likersRes.value ?? [])     : (errs.fans      = true, []);
      const orderList  = ordersRes.status     === 'fulfilled'
        ? (ordersRes.value?.content ?? ordersRes.value ?? [])
        : (errs.clients = true, []);

      setMembers(buildAudienceMap(affiliates, likers, orderList));
      setErrors(errs);
      setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [providerData.id]);

  const filtered = members.filter((m) => {
    if (filter === 'affiliated' && !m.isAffiliated) return false;
    if (filter === 'fan'        && !m.isFan)        return false;
    if (filter === 'lead'       && !m.isLead)       return false;
    if (filter === 'client'     && !m.isClient)     return false;
    if (search && !m.name?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const totals = {
    affiliated: members.filter((m) => m.isAffiliated).length,
    fan:        members.filter((m) => m.isFan).length,
    lead:       members.filter((m) => m.isLead).length,
    client:     members.filter((m) => m.isClient).length,
  };

  const FILTERS = [
    { key: 'all',        label: t('audience.filterAll') },
    { key: 'affiliated', label: t('audience.affiliated') },
    { key: 'fan',        label: t('audience.fan') },
    { key: 'lead',       label: t('audience.lead') },
    { key: 'client',     label: t('audience.client') },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {errors.affiliates && <Alert severity="warning" sx={{ mb: 1 }}>{t('audience.affiliatesLoadFailed')}</Alert>}
      {errors.fans       && <Alert severity="warning" sx={{ mb: 1 }}>{t('audience.fansLoadFailed')}</Alert>}
      {errors.clients    && <Alert severity="warning" sx={{ mb: 1 }}>{t('audience.ordersLoadFailed')}</Alert>}

      {/* Summary */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('audience.summary', { affiliated: totals.affiliated, fans: totals.fan, leads: totals.lead, clients: totals.client, total: members.length })}
      </Typography>

      {/* Filter chips */}
      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
        {FILTERS.map(({ key, label }) => (
          <Chip
            key={key}
            label={label}
            onClick={() => setFilter(key)}
            color={filter === key ? 'primary' : 'default'}
            variant={filter === key ? 'filled' : 'outlined'}
            size="small"
          />
        ))}
      </Stack>

      {/* Search */}
      <TextField
        size="small"
        placeholder={t('audience.searchPlaceholder')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
            </InputAdornment>
          ),
        }}
      />

      {/* Empty state */}
      {filtered.length === 0 && (
        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 5, textAlign: 'center' }}>
          <PeopleAltIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">
            {members.length === 0 ? t('audience.emptyNone') : t('audience.emptyFiltered')}
          </Typography>
        </Paper>
      )}

      {/* Cards */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {filtered.map((m) => (
          <AudienceCard
            key={m.userId}
            member={m}
            onNavigateToOrders={onNavigateToOrders}
            showOrders={showOrders}
          />
        ))}
      </Box>
    </Box>
  );
};

export default MyAudience;
